/* eslint-disable no-undef */
import {
  Autocomplete,
  Box,
  Button,
  Grid2 as Grid,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import IntlMessages from "../../components/IntlMessages/IntlMessages";
import { UNInput, UNModal, UNSwitch } from "un-react-ui-components";
import { GRAY_50, GRAY_BLACK } from "../../themes/colors";
import { isValidEmail } from "../../util/utils";
import { notify } from "../../libs/observables/notification";
import { useRequestsContext } from "../../contexts/requests/RequestsContext";
import {
  InvitationCreateData,
  InvitationType,
} from "../../core/models/Invitation";
import { useInvitationsActions } from "../../contexts/invitations/UseInvitationsActions";
import { useInvitationsContext } from "../../contexts/invitations/InvitationsContext";
import TextField from "@mui/material/TextField";
import { useProfilesActions } from "../../contexts/usersProfile/UseProfilesActions";
import { DEFAULT_PAGE_SIZE } from "../../core/services/network/NetworkTypes";
import { useProfilesContext } from "../../contexts/usersProfile/ProfilesContext";
import Profile from "../../core/models/Profile";
import { useIntl } from "react-intl";
type Option = {
  value: string;
  labelParts: [string, string, string];
};
type NewInvitationModalProps = {
  open: boolean;
  onClose(): void;
};
const NewInvitationModal = ({ open, onClose }: NewInvitationModalProps) => {
  const intl = useIntl();
  const [orgUserId, setOrgUserId] = useState("");
  const [errorEmail, setErrorEmail] = useState("");
  const [isInviteByEmail, setIsInviteByEmail] = useState(true);
  const [isPushNotfication, setIsPushNotfication] = useState(false);
  const { selectedRequest } = useRequestsContext();
  const { dispatchCreateInvitation } = useInvitationsActions();
  const { selectedInvitation, invitationsError, setSelectedInvitation } =
    useInvitationsContext();
  const [loading, setLoading] = useState<boolean>(false);

  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<Option[]>();
  const [value, setValue] = useState<Option | null>(null);
  const { profilesResponseData, profilesLoading, setProfilesLoading } =
    useProfilesContext();
  const { dispatchGetProfiles } = useProfilesActions();
  const notValidEmail = intl.formatMessage({ id: "invitation.email.notValid" });
  const requiredEmail = intl.formatMessage({ id: "invitation.email.required" });

  //#region Load users profiles
  const loadUsersProfile = (search?: string) => {
    dispatchGetProfiles({
      filter: {
        search,
      },
      page: 1,
      size: DEFAULT_PAGE_SIZE,
    }).catch();
  };
  const onSearch = (search: string) => {
    loadUsersProfile(search);
  };
  useEffect(() => {
    if (selectedInvitation && !invitationsError) {
      notify("success")({ message: "Invitation sent!" });
      setValue(null);
      setOrgUserId("");
      setIsInviteByEmail(true);
      setIsPushNotfication(false);
      setSelectedInvitation();
      onClose();
    }
    setProfilesLoading(false);
  }, [selectedInvitation]);

  useEffect(() => {
    if (profilesResponseData) {
      const results = profilesResponseData.data.map((item: Profile) => ({
        value: item.email,
        labelParts: [
          `${item.firstName} ${item.lastName}`,
          item.email,
          item.organization,
        ] as [string, string, string],
      }));
      setOptions(results);
    }
  }, [profilesResponseData]);

  useEffect(() => {
    if (!inputValue) return;

    setProfilesLoading(true);
    const delayDebounce = setTimeout(() => {
      // Your debounced API call
      onSearch(inputValue);
    }, 500); // ← this parenthesis closes setTimeout properly

    // Cleanup on unmount or input change
    return () => clearTimeout(delayDebounce);
  }, [inputValue]);

  //#endregion

  //#region Actions modal
  const onSave = () => {
    if (!value) {
      setErrorEmail(requiredEmail);
      return;
    }
    if (value && !isValidEmail(value.value)) {
      setErrorEmail(notValidEmail);
      return;
    }
    if (!isInviteByEmail && !isPushNotfication) {
      notify("error")({ message: "Choose at least one invitation method" });
      return;
    }

    // Construct the invitationTypes array safely
    const invitationTypes: InvitationType[] = [];
    if (isInviteByEmail) invitationTypes.push(InvitationType.email);
    if (isPushNotfication)
      invitationTypes.push(InvitationType.push_notification);
    if (value) {
      const invitation: InvitationCreateData = {
        email: value.value,
        integration_configuration: selectedRequest?.id,
        invitation_types: invitationTypes,
        organization_user_uid: orgUserId,
      };
      setLoading(true);
      dispatchCreateInvitation(invitation);
    }
  };

  //#region end
  const sanitize = (value: string | null | undefined) =>
    !value || value === "null" ? "-" : value;

  const formatLabel = (parts: (string | null | undefined)[]) => {
    const [nameRaw, emailRaw, orgRaw] = parts;
    const name = sanitize(nameRaw);
    const email = sanitize(emailRaw);
    const org = sanitize(orgRaw);

    return `${name.padEnd(50)}${email.padEnd(45)}${org}`;
  };
  //#region Renderers
  const content = (
    <Box className="space-y-12">
      <Grid
        data-testid="newStepModal.filters"
        container
        alignItems="center"
        direction={"row"}
      >
        <Grid size={{ xs: 12 }} className="space-y-[1.875rem]">
          <Typography variant="h6" color={GRAY_BLACK}>
            {selectedRequest?.title}
          </Typography>
          {/* <UNInput
            id="invitation.email"
            data-testid={"invitation.email"}
            labelTranslationKey={"invitation.email"}
            value={email}
            onChange={setEmail}
            error={errorEmail}
          /> */}
          <Box>
            <Typography variant="body2" color={GRAY_50}>
              <IntlMessages id="invitation.email" />
            </Typography>
            <Autocomplete
              freeSolo
              data-testid={"invitation.email"}
              options={options || []}
              getOptionLabel={(option) =>
                typeof option === "string"
                  ? option
                  : formatLabel(option.labelParts)
              }
              filterOptions={(x) => x}
              value={value}
              inputValue={inputValue}
              onInputChange={(_, newInputValue, reason) => {
                setInputValue(newInputValue);
                if (reason === "clear") {
                  setErrorEmail("");
                  setValue(null);
                }
              }}
              onChange={(_, newValue) => {
                if (typeof newValue === "string") {
                  setValue({ value: newValue, labelParts: [newValue, "", ""] });
                  setInputValue(newValue);
                } else {
                  setValue(newValue);
                  setInputValue(newValue?.value ?? "");
                }

                if (
                  newValue &&
                  typeof newValue !== "string" &&
                  !isValidEmail(newValue.value)
                ) {
                  setErrorEmail(notValidEmail);
                } else {
                  setErrorEmail("");
                }
              }}
              onBlur={() => {
                // user typed something and left the input
                if (!value && inputValue) {
                  if (!isValidEmail(inputValue)) {
                    setErrorEmail(notValidEmail);
                  } else {
                    setValue({
                      value: inputValue,
                      labelParts: [inputValue, "", ""],
                    });
                    setErrorEmail("");
                  }
                }
              }}
              loading={profilesLoading}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    pr: 2,
                  }}
                >
                  <Typography style={{ flex: 1 }}>
                    {option.labelParts[0]}
                  </Typography>
                  <Typography style={{ flex: 1 }}>
                    {option.labelParts[1]}
                  </Typography>
                  <Typography style={{ flex: 1 }}>
                    {option.labelParts[2]}
                  </Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={!!errorEmail}
                  helperText={errorEmail}
                />
              )}
            />
          </Box>
          <UNInput
            id="invitation.new.organization.id"
            data-testid={"invitation.new.organization.id"}
            labelTranslationKey={"invitation.new.organization.id"}
            value={orgUserId}
            onChange={setOrgUserId}
          />
          <Grid size={{ xs: 12 }} className="space-y-[1.0rem] pb-8">
            <UNSwitch
              label={undefined}
              labelTranslationKey="invitation.new.byEmail"
              labelTranslationKeyParam={undefined}
              value={isInviteByEmail}
              onChange={setIsInviteByEmail}
            />
            <UNSwitch
              label={undefined}
              labelTranslationKey="invitation.new.push.notification"
              labelTranslationKeyParam={undefined}
              value={isPushNotfication}
              onChange={setIsPushNotfication}
            />
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );

  const actions = (
    <>
      <Box />
      <Box className="flex space-x-[1.375rem]">
        <Button
          data-testid="newStepModal.cancel"
          variant="text"
          color="error"
          onClick={() => onClose()}
        >
          <IntlMessages id="cancel" />
        </Button>
        <Button
          data-testid="newStepModal.save"
          onClick={onSave}
          //disabled={loading}
        >
          <IntlMessages id="save" />
        </Button>
      </Box>
    </>
  );
  //#endregion

  return (
    <UNModal
      id="newInvitationModal"
      titleTranslationKey="invitation.new.title"
      showTitleBorder={false}
      open={open}
      loading={loading}
      onClose={() => onClose()}
      actions={actions}
      maxWidth={"md"}
    >
      {content}
    </UNModal>
  );
};

export { NewInvitationModal };
